import { useCallback } from 'react';
import { Editor } from '@tiptap/react';

export function useToolbarActions(
  editor: Editor | null,
  setBubbleView?: (value: string | boolean) => void,
) {
  const toggleUndo = useCallback(() => {
    editor?.chain().focus().undo().run();
  }, [editor]);

  const toggleRedo = useCallback(() => {
    editor?.chain().focus().redo().run();
  }, [editor]);

  const toggleBold = useCallback(() => {
    editor?.chain().focus().toggleBold().run();
  }, [editor]);

  const toggleInlineNote = useCallback(() => {
    editor?.chain().focus().toggleInlineNote().run();
  }, [editor]);

  const togglePrintTrim = useCallback(() => {
    editor?.chain().focus().togglePrintTrim().run();
  }, [editor]);

  const toggleItalic = useCallback(() => {
    editor?.chain().focus().toggleItalic().run();
  }, [editor]);

  const toggleUnderline = useCallback(() => {
    editor?.chain().focus().toggleUnderline().run();
  }, [editor]);

  const toggleBulletList = useCallback(() => {
    editor?.chain().focus().toggleBulletList().run();
  }, [editor]);

  const toggleOrderedList = useCallback(() => {
    editor?.chain().focus().toggleOrderedList().run();
  }, [editor]);

  const toggleBlockQuote = useCallback(() => {
    editor?.chain().focus().toggleBlockquote().run();
  }, [editor]);

  const insertHorizontalRule = useCallback(() => {
    editor?.chain().focus().setHorizontalRule().run();
  }, [editor]);

  const insertPageBreak = useCallback(() => {
    editor?.chain().focus().setPageBreak().run();
  }, [editor]);

  const insertTable = useCallback(() => {
    editor?.chain().focus().insertTable().run();
  }, [editor]);

  const editLink = useCallback(() => {
    if (!editor) return;

    if (setBubbleView) {
      const { from, to } = editor.state.selection;
      setBubbleView('linkEditor');

      // Mark that we're doing a programmatic selection change
      editor.storage.isToolbarToggle = true;

      // Force shouldShow to re-evaluate by slightly adjusting selection then restoring it
      queueMicrotask(() => {
        // Trigger bubble menue shouldShow re-evaluation
        editor.commands.setTextSelection({ from, to: to - 1 });

        // Immediately restore original selection
        queueMicrotask(() => {
          editor.commands.setTextSelection({ from, to });
          // Clear the flag after toggle is complete
          setTimeout(() => {
            editor.storage.isToolbarToggle = false;
          }, 50);
        });
      });
    } else {
      editor.storage.bubbleMenu = { view: 'linkEditor' };
    }
  }, [editor, setBubbleView]);

  return {
    toggleUndo,
    toggleRedo,
    toggleBold,
    toggleInlineNote,
    togglePrintTrim,
    toggleItalic,
    toggleUnderline,
    toggleBulletList,
    toggleOrderedList,
    toggleBlockQuote,
    insertHorizontalRule,
    insertPageBreak,
    insertTable,
    editLink,
  };
}
